import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ThemeProvider } from './utils/theme';
import './App.css';
import SearchBar from './components/SearchBar';
import SearchResults from './components/SearchResults';
import Filters from './components/Filters';
import SortFilter from './components/SortFilter';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import AuthModal from './components/AuthModal';
import SaveToCollectionModal from './components/SaveToCollectionModal';
import RoleSelectionModal from './components/RoleSelectionModal';
import SupportModal from './components/SupportModal';
import WhatsNewModal, { APP_VERSION } from './components/WhatsNewModal';
import ClaimAccountBanner from './components/ClaimAccountBanner';
import Collections from './pages/Collections';
import Teams from './pages/Teams';
import Membership from './pages/Membership';
import Enterprise from './pages/Enterprise';
import Monitors from './pages/Monitors';
import Admin from './pages/Admin';
import Dashboard from './pages/Dashboard';
import Docs from './pages/Docs';
import GrantAgent from './pages/GrantAgent';
import PaperAgent from './pages/PaperAgent';
import PIProfile from './pages/PIProfile';
import LifecycleTracker from './pages/LifecycleTracker';
import Calendar from './pages/Calendar';
import Funding from './pages/Funding';
import Onboarding from './pages/Onboarding';
import SampleLibrary from './pages/SampleLibrary';
import InstantReview from './pages/InstantReview';
import { getToken, setToken, getAuthHeaders, isAuthenticated, removeToken } from './utils/auth';
import { getApiEndpoint } from './utils/api';
import { getOnboardingState, completeOnboarding, isMagicLinkUser, updateOnboarding } from './utils/onboarding';
import analytics from './utils/analytics';

// Build a unified OpenAlex filter string from the filter state object.
function buildFilterString(activeFilters, additionalFilters = '') {
  const parts = [];
  if (additionalFilters) parts.push(additionalFilters);
  if (activeFilters.publicationYear) parts.push(`publication_year:${activeFilters.publicationYear}`);
  if (activeFilters.openAccess) parts.push(activeFilters.openAccess);
  if (activeFilters.type) parts.push(activeFilters.type);
  // authors is now an array of { id, display_name }; ignore legacy string values
  if (Array.isArray(activeFilters.authors) && activeFilters.authors.length > 0) {
    const ids = activeFilters.authors.map(a => a.id.replace('https://openalex.org/', '')).join('|');
    parts.push(`author.id:${ids}`);
  }
  if (activeFilters.topics && activeFilters.topics.length > 0) {
    parts.push(`topics.id:${activeFilters.topics.map(t => t.id).join('|')}`);
  }
  // Journal facets
  const includedJournals = (activeFilters.journals || []).filter(j => !j.excluded).map(j => j.id);
  const excludedJournals = (activeFilters.journals || []).filter(j => j.excluded).map(j => j.id);
  if (includedJournals.length > 0) parts.push(`primary_location.source.id:${includedJournals.join('|')}`);
  excludedJournals.forEach(id => parts.push(`primary_location.source.id:!${id}`));
  // Institution facets
  const includedInstitutions = (activeFilters.institutions || []).filter(i => !i.excluded).map(i => i.id);
  const excludedInstitutions = (activeFilters.institutions || []).filter(i => i.excluded).map(i => i.id);
  if (includedInstitutions.length > 0) parts.push(`authorships.institutions.id:${includedInstitutions.join('|')}`);
  excludedInstitutions.forEach(id => parts.push(`authorships.institutions.id:!${id}`));
  return parts.filter(Boolean).join(',');
}

function App() {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState(null);
  const [facets, setFacets] = useState({ journals: [], institutions: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState('relevance_score:desc');
  const [searchOptions, setSearchOptions] = useState({ field: 'title_and_abstract', mode: 'boolean' });
  const [perPage] = useState(25);
  const [searchHistory, setSearchHistory] = useState([]);
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showWhatsNewModal, setShowWhatsNewModal] = useState(false);
  const [lastSeenVersion, setLastSeenVersion] = useState('');
  const [workToSave, setWorkToSave] = useState(null);
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard', 'search', 'collections', 'teams', 'membership', 'enterprise', 'monitors', 'docs'
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('sb_collapsed') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('sb_collapsed', sidebarCollapsed ? '1' : '0'); } catch {}
  }, [sidebarCollapsed]);
  const [selectedEnterpriseId, setSelectedEnterpriseId] = useState(null);
  const [pendingCollectionId, setPendingCollectionId] = useState(null);
  const [pendingIdea, setPendingIdea] = useState(null);
  const [pendingProposalId, setPendingProposalId] = useState(null);
  const [pendingDraftId, setPendingDraftId] = useState(null);
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [authModalInitialIsLogin, setAuthModalInitialIsLogin] = useState(true);

  const fetchFacets = useCallback(async (query, filterStr, options = null) => {
    if (!query || !query.trim()) return;
    try {
      const activeOptions = options || searchOptions;
      const params = new URLSearchParams({ search: query });
      if (filterStr) params.append('filter', filterStr);
      params.append('search_field', activeOptions.field);
      params.append('search_mode', activeOptions.mode);
      const response = await fetch(getApiEndpoint(`/api/works/facets?${params.toString()}`));
      if (response.ok) {
        const data = await response.json();
        setFacets(data);
      }
    } catch (err) {
      console.error('Error fetching facets:', err);
    }
  }, [searchOptions]);

  const performSearch = useCallback(async (query, page = 1, additionalFilters = '', filtersToUse = null, sortToUse = null, optionsToUse = null) => {
    if (!query.trim()) return;

    setLoading(true);
    setError(null);

    try {
      // Use provided filters or fall back to state
      const activeFilters = filtersToUse !== null ? filtersToUse : filters;
      const activeSort = sortToUse !== null ? sortToUse : sortBy;
      const activeOptions = optionsToUse !== null ? optionsToUse : searchOptions;

      // Build unified filter string
      const filterStr = buildFilterString(activeFilters, additionalFilters);

      const params = new URLSearchParams({
        search: query,
        page: page.toString(),
        per_page: perPage.toString()
      });

      if (filterStr) {
        params.append('filter', filterStr);
      }

      // Add sort parameter
      if (activeSort) {
        params.append('sort', activeSort);
      }

      params.append('search_field', activeOptions.field);
      params.append('search_mode', activeOptions.mode);

      const response = await fetch(getApiEndpoint(`/api/works?${params.toString()}`));
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Failed to fetch results');
      }

      // Debug: Log response to check per_page
      if (data.meta) {
        console.log('Search response:', {
          requested_per_page: perPage,
          actual_per_page: data.meta.per_page,
          results_count: data.results?.length || 0,
          total_count: data.meta.count
        });
      }

      setResults(data);
      setCurrentPage(page);

      // Kick off facets in background (no await — non-blocking)
      fetchFacets(query, filterStr, activeOptions);

      // Update URL
      const newUrl = new URL(window.location);
      newUrl.searchParams.set('q', query);
      newUrl.searchParams.set('page', page.toString());
      window.history.pushState({}, '', newUrl);
    } catch (err) {
      setError(err.message);
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, [filters, sortBy, perPage, fetchFacets, searchOptions]);

  const handleCitationSearchPagination = useCallback(async (workId, type, page, sortToUse = null) => {
    setLoading(true);
    setError(null);

    try {
      const endpoint = type === 'cites' ? 'cites' : type === 'cited_by' ? 'cited_by' : 'related';
      const activeSort = sortToUse !== null ? sortToUse : sortBy;
      
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '200'
      });
      
      if (activeSort) {
        params.append('sort', activeSort);
      }
      
      const response = await fetch(getApiEndpoint(`/api/works/${workId}/${endpoint}?${params.toString()}`));
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Failed to fetch results');
      }

      setResults(data);
      setCurrentPage(page);

      // Update URL
      const newUrl = new URL(window.location);
      newUrl.searchParams.set('page', page.toString());
      window.history.pushState({}, '', newUrl);
    } catch (err) {
      setError(err.message);
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, [sortBy]);

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
    fetchTrialStatus();
  }, []);

  // Fire a manual pageview whenever the view changes (we run our own routing).
  useEffect(() => {
    analytics.pageview(currentView, { authenticated: !!user });
  }, [currentView, user]);

  // Load search from URL params on mount and handle routing
  useEffect(() => {
    const path = window.location.pathname;

    // Handle magic link login (/m/{token}) — auto-create account + redirect
    if (path.startsWith('/m/')) {
      const magicToken = path.slice(3); // strip '/m/'
      if (magicToken) {
        fetch(getApiEndpoint(`/api/auth/magic/${magicToken}`))
          .then(r => {
            if (!r.ok) throw new Error('Invalid or expired link');
            return r.json();
          })
          .then(data => {
            setToken(data.access_token);
            // Magic link users have pre-built data — skip onboarding
            if (data.user && data.user.id) {
              completeOnboarding(data.user.id);
            }
            // Stitch the anon session into the identified user, then capture the wedge event.
            if (data.user && data.user.ph_distinct_id) {
              analytics.alias(data.user.ph_distinct_id);
              analytics.identify(
                data.user.ph_distinct_id,
                { source_campaign: data.user.source_campaign || null },
                { source_campaign: data.user.source_campaign || null, signup_method: 'magic_link' }
              );
            }
            analytics.track('magic_link_redeemed', {
              source_campaign: data.user && data.user.source_campaign,
              redirect_path: data.redirect_path || '/',
            });
            const redirect = data.redirect_path || '/';
            window.location.href = redirect;
          })
          .catch(err => {
            console.error('Magic link error:', err);
            // Clear the /m/ path and go home
            window.history.replaceState({}, '', '/');
          });
        return; // don't process further while magic link resolves
      }
    }

    // Handle URL-based navigation (from email links, direct URLs, etc.)
    const viewRoutes = {
      '/instant-review': 'instant-review',
      '/signup': 'dashboard',
      '/grant-agent': 'grant-agent',
      '/paper-agent': 'paper-agent',
      '/collections': 'collections',
      '/funding': 'funding',
      '/monitors': 'monitors',
      '/docs': 'docs',
      '/pi-profile': 'pi-profile',
      '/calendar': 'calendar',
      '/teams': 'teams',
    };
    for (const [route, view] of Object.entries(viewRoutes)) {
      if (path === route || path.startsWith(route + '/')) {
        if (view === 'grant-agent') {
          const pid = new URLSearchParams(window.location.search).get('proposal_id');
          if (pid) {
            const parsed = parseInt(pid, 10);
            if (!Number.isNaN(parsed)) setPendingProposalId(parsed);
          }
        }
        setCurrentView(view);
        return;
      }
    }

    // Handle membership success redirect
    if (path === '/membership/success' || path.startsWith('/membership')) {
      setCurrentView('membership');
      return;
    }

    // Handle enterprise view
    if (path.startsWith('/enterprise')) {
      setCurrentView('enterprise');
      return;
    }
    
    // Handle search URL params
    const params = new URLSearchParams(window.location.search);
    const urlQuery = params.get('q');
    const urlPage = parseInt(params.get('page')) || 1;
    const urlFilters = params.get('filter');
    const citationType = params.get('type');
    const workId = params.get('work_id');

    // Check if this is a citation search
    if (citationType && workId) {
      handleCitationSearchPagination(workId, citationType, urlPage);
      if (urlQuery) {
        setSearchQuery(urlQuery);
      }
    } else if (urlQuery) {
      setSearchQuery(urlQuery);
      setCurrentPage(urlPage);
      if (urlFilters) {
        // Parse filters from URL
        const filterObj = {};
        urlFilters.split(',').forEach(filter => {
          const [key, value] = filter.split(':');
          if (key && value) {
            filterObj[key] = value;
          }
        });
        setFilters(filterObj);
      }
      performSearch(urlQuery, urlPage, urlFilters);
    }
  }, [handleCitationSearchPagination, performSearch]);

  const checkAuth = async (fromLogin = false) => {
    if (isAuthenticated()) {
      try {
        const token = getToken();
        const headers = {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        };

        // Debug logging
        console.log('Checking auth, token exists:', !!token);
        console.log('Auth headers:', headers);

        const response = await fetch(getApiEndpoint('/api/auth/me'), {
          method: 'GET',
          headers: headers
        });

        console.log('Auth check response status:', response.status);

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          if (userData.ph_distinct_id) {
            analytics.identify(
              userData.ph_distinct_id,
              {
                role: userData.user_role || null,
                source_campaign: userData.source_campaign || null,
                has_password: !!userData.has_password,
              },
              { source_campaign: userData.source_campaign || null }
            );
          }
          // Show role modal on first login if role not set
          if (fromLogin && !userData.user_role) {
            setShowRoleModal(true);
          } else if (fromLogin && userData.user_role) {
            // Returning user — go to dashboard
            setCurrentView('dashboard');
            // Only show What's New to returning users who have seen a prior version
            const obState = getOnboardingState(userData.id);
            if (obState.whats_new_version) {
              // Returning user — show if version changed
              const seen = obState.whats_new_version;
              setLastSeenVersion(seen);
              if (seen !== APP_VERSION) {
                setShowWhatsNewModal(true);
              }
            }
            // For new users (no prior version), skip
          }
        } else {
          const errorText = await response.text();
          console.error('Auth check failed:', response.status, errorText);
          removeToken();
          setUser(null);
        }
      } catch (err) {
        console.error('Auth check error:', err);
        removeToken();
        setUser(null);
      }
    }
  };

  const fetchTrialStatus = async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/user/subscription'), {
        headers: getAuthHeaders()
      });
      if (!response.ok) return;
      const data = await response.json();
      if (data.status === 'trialing' && data.trial_end) {
        const daysLeft = Math.ceil(
          (new Date(data.trial_end) - new Date()) / (1000 * 60 * 60 * 24)
        );
        setTrialDaysLeft(daysLeft > 0 ? daysLeft : null);
      } else {
        setTrialDaysLeft(null);
      }
    } catch (err) {
      // non-critical, ignore
    }
  };

  const handleLoginSuccess = async () => {
    // Small delay to ensure token is saved to localStorage
    await new Promise(resolve => setTimeout(resolve, 100));
    await checkAuth(true);
    await fetchTrialStatus();
  };

  const handleRoleSelected = (updatedUser) => {
    setUser(updatedUser);
    setShowRoleModal(false);
    localStorage.setItem('mnevia_whats_new_seen', APP_VERSION);
    if (updatedUser && updatedUser.id) {
      updateOnboarding(updatedUser.id, { whats_new_version: APP_VERSION });
    }
    // Always go to dashboard — onboarding flow will show for new users
    setCurrentView('dashboard');
  };

  const handleWhatsNewClose = () => {
    localStorage.setItem('mnevia_whats_new_seen', APP_VERSION);
    if (user && user.id) {
      updateOnboarding(user.id, { whats_new_version: APP_VERSION });
    }
    setShowWhatsNewModal(false);
  };

  const handleLogout = () => {
    analytics.reset();
    setUser(null);
    setCurrentView('dashboard');
  };

  const openAuthModal = (isLogin = true) => {
    setAuthModalInitialIsLogin(isLogin);
    setShowAuthModal(true);
  };

  const handleSaveToCollection = (work) => {
    if (!isAuthenticated()) {
      openAuthModal(false);
      return;
    }
    setWorkToSave(work);
    setShowSaveModal(true);
  };

  const handleSaveSuccess = () => {
    setShowSaveModal(false);
    setWorkToSave(null);
  };

  const handleSearch = (query) => {
    // Clear search history when starting a new search
    setSearchHistory([]);
    setSearchQuery(query);
    setCurrentPage(1);
    performSearch(query, 1, '');
  };

  const handleSearchOptionsChange = (newOptions) => {
    setSearchOptions(newOptions);
    if (searchQuery) {
      performSearch(searchQuery, 1, '', null, null, newOptions);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    if (searchQuery) {
      // Pass newFilters directly to ensure they're used immediately
      performSearch(searchQuery, 1, '', newFilters, null);
    }
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    
    // Check if this is a citation search (has type and work_id in URL)
    const params = new URLSearchParams(window.location.search);
    const citationType = params.get('type');
    const workId = params.get('work_id');
    
    if (citationType && workId) {
      // This is a citation search, handle sort change differently
      handleCitationSearchPagination(workId, citationType, 1, newSort);
    } else if (searchQuery) {
      // Regular search
      performSearch(searchQuery, 1, '', null, newSort);
    }
  };

  const handlePageChange = (newPage) => {
    // Check if this is a citation search (has type and work_id in URL)
    const params = new URLSearchParams(window.location.search);
    const citationType = params.get('type');
    const workId = params.get('work_id');

    if (citationType && workId) {
      // This is a citation search, handle pagination differently
      handleCitationSearchPagination(workId, citationType, newPage);
    } else if (searchQuery) {
      performSearch(searchQuery, newPage, '', null, null);
    }
  };

  // Apply client-side relevance score threshold without re-fetching from API
  const filteredResults = useMemo(() => {
    if (!results) return null;

    const thresholdValue = filters?.relevanceThreshold;
    const threshold = thresholdValue === undefined || thresholdValue === null || thresholdValue === ''
      ? null
      : parseFloat(thresholdValue);

    if (threshold === null || Number.isNaN(threshold)) {
      return results;
    }

    const filteredWorks = (results.results || []).filter(work => {
      const score = work?.relevance_score;
      return typeof score === 'number' ? score >= threshold : false;
    });

    return {
      ...results,
      results: filteredWorks,
      meta: {
        ...results.meta,
        filtered_count: filteredWorks.length,
      }
    };
  }, [results, filters]);

  const handleCitationSearch = async (work, type) => {
    if (!work || !work.id) return;

    // Always save current search state before navigating to citation search
    // This works for both regular searches and chained citation searches
    if (searchQuery && results) {
      const currentState = {
        query: searchQuery,
        results: results,
        page: currentPage,
        filters: { ...filters },
        sortBy: sortBy,
        isCitationSearch: window.location.search.includes('type=')
      };
      
      setSearchHistory(prev => [...prev, currentState]);
    }

    setLoading(true);
    setError(null);
    setCurrentPage(1);

    try {
      const workId = work.id.replace('https://openalex.org/', '');
      const endpoint = type === 'cites' ? 'cites' : type === 'cited_by' ? 'cited_by' : 'related';
      
      const params = new URLSearchParams({
        page: '1',
        per_page: '200'
      });
      
      if (sortBy) {
        params.append('sort', sortBy);
      }
      
      const response = await fetch(getApiEndpoint(`/api/works/${workId}/${endpoint}?${params.toString()}`));
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Failed to fetch results');
      }

      // Set a descriptive search query for display
      const queryLabel = type === 'cites' 
        ? `Works cited by: ${work.display_name || work.title || 'This work'}`
        : type === 'cited_by'
        ? `Works citing: ${work.display_name || work.title || 'This work'}`
        : `Related to: ${work.display_name || work.title || 'This work'}`;

      setSearchQuery(queryLabel);
      setResults(data);
      setCurrentPage(1);

      // Update URL
      const newUrl = new URL(window.location);
      newUrl.searchParams.set('q', encodeURIComponent(queryLabel));
      newUrl.searchParams.set('page', '1');
      newUrl.searchParams.set('type', type);
      newUrl.searchParams.set('work_id', workId);
      window.history.pushState({}, '', newUrl);
    } catch (err) {
      setError(err.message);
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (searchHistory.length > 0) {
      // Get the last search from history
      const lastSearch = searchHistory[searchHistory.length - 1];
      
      // Restore the search state
      setSearchQuery(lastSearch.query);
      setResults(lastSearch.results);
      setCurrentPage(lastSearch.page);
      setFilters(lastSearch.filters);
      setSortBy(lastSearch.sortBy);
      
      // Remove the last entry from history
      setSearchHistory(prev => prev.slice(0, -1));

      // Update URL
      const newUrl = new URL(window.location);
      if (lastSearch.isCitationSearch) {
        // If it was a citation search, we need to reconstruct the URL
        // But we don't have the work_id and type, so we'll just use the query
        newUrl.searchParams.set('q', lastSearch.query);
        newUrl.searchParams.set('page', lastSearch.page.toString());
      } else {
        // Regular search
        newUrl.searchParams.set('q', lastSearch.query);
        newUrl.searchParams.set('page', lastSearch.page.toString());
        newUrl.searchParams.delete('type');
        newUrl.searchParams.delete('work_id');
      }
      window.history.pushState({}, '', newUrl);
    }
  };

  return (
    <ThemeProvider>
    <div className="App">
      {isAuthenticated() && user ? (
        <Sidebar
          currentView={currentView}
          user={user}
          onNavigate={(view) => { setCurrentView(view); }}
          collapsed={sidebarCollapsed}
          onCollapsedChange={setSidebarCollapsed}
        />
      ) : null}
      <Header
        user={user}
        onLoginClick={() => openAuthModal(true)}
        onLogout={handleLogout}
        onPIProfileClick={() => { setCurrentView('pi-profile'); }}
      />
      <div className={`container ${isAuthenticated() && user ? 'container--with-sidebar' : ''} ${isAuthenticated() && user && sidebarCollapsed ? 'container--with-sidebar--collapsed' : ''}`}>
        {trialDaysLeft !== null && currentView !== 'membership' && (
          <div className="trial-banner">
            <span>
              ⏳ <strong>{trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''}</strong> left in your free Pro trial
            </span>
            <button className="trial-banner-cta" onClick={() => setCurrentView('membership')}>
              Upgrade to keep access →
            </button>
          </div>
        )}
        <ClaimAccountBanner
          user={user}
          onPasswordSet={() => {
            // Refresh user so has_password flag updates
            if (user) setUser({ ...user, has_password: true });
          }}
        />
        {currentView === 'instant-review' ? (
          <InstantReview
            user={user}
            onLogin={() => openAuthModal(true)}
            onStartGrantAgent={(proposalId) => {
              setPendingProposalId(proposalId);
              setCurrentView('grant-agent');
            }}
          />
        ) : currentView === 'dashboard' && user && !isMagicLinkUser(user) && !getOnboardingState(user.id).onboarding_complete ? (
          <Onboarding
            user={user}
            onComplete={() => { completeOnboarding(user.id); setCurrentView('dashboard'); }}
            onNavigate={(view) => { completeOnboarding(user.id); setCurrentView(view); }}
          />
        ) : currentView === 'dashboard' && user ? (
          <Dashboard
            user={user}
            onNavigate={(view) => { setCurrentView(view); }}
            onOpenProposal={(proposalId) => {
              setPendingProposalId(proposalId);
              setCurrentView('grant-agent');
            }}
            onOpenDraft={(draftId) => {
              setPendingCollectionId(null);
              setPendingIdea(null);
              setPendingDraftId(draftId);
              setCurrentView('paper-agent');
            }}
          />
        ) : currentView === 'collections' ? (
          <Collections
            user={user}
            onBack={() => setCurrentView('dashboard')}
            onCitationSearch={(work, type) => {
              setCurrentView('search');
              handleCitationSearch(work, type);
            }}
            onStartGrantAgent={(collectionId, idea, proposalId) => {
              setPendingCollectionId(collectionId);
              setPendingIdea(idea || null);
              setPendingProposalId(proposalId || null);
              setCurrentView('grant-agent');
            }}
            onStartPaperAgent={(collectionId, idea) => {
              setPendingCollectionId(collectionId);
              setPendingIdea(idea || null);
              setCurrentView('paper-agent');
            }}
            initialCollectionId={pendingCollectionId}
            onMounted={() => setPendingCollectionId(null)}
          />
        ) : currentView === 'teams' ? (
          <Teams onBack={() => setCurrentView('dashboard')} onNavigate={(view) => setCurrentView(view)} />
        ) : currentView === 'monitors' ? (
          <Monitors onBack={() => setCurrentView('dashboard')} />
        ) : currentView === 'membership' ? (
          <Membership
            onBack={() => setCurrentView('dashboard')}
            onEnterpriseClick={(accountId) => {
              setSelectedEnterpriseId(accountId);
              setCurrentView('enterprise');
            }}
          />
        ) : currentView === 'enterprise' ? (
          <Enterprise onBack={() => setCurrentView('membership')} accountId={selectedEnterpriseId} />
        ) : currentView === 'admin' ? (
          <Admin onBack={() => setCurrentView('dashboard')} />
        ) : currentView === 'docs' ? (
          <Docs onNavigate={(view) => setCurrentView(view)} />
        ) : currentView === 'funding' ? (
          <Funding onStartGrantAgent={(collectionId, idea, proposalId) => {
            setPendingProposalId(proposalId);
            setCurrentView('grant-agent');
          }} />
        ) : currentView === 'sample-library' ? (
          <SampleLibrary />
        ) : currentView === 'calendar' ? (
          <Calendar />
        ) : currentView === 'lifecycle' ? (
          <LifecycleTracker />
        ) : currentView === 'pi-profile' ? (
          <PIProfile user={user} />
        ) : currentView === 'grant-agent' ? (
          <GrantAgent initialCollectionId={pendingCollectionId} initialIdea={pendingIdea} initialProposalId={pendingProposalId} user={user} onMounted={() => { setPendingCollectionId(null); setPendingIdea(null); setPendingProposalId(null); }} />
        ) : currentView === 'paper-agent' ? (
          <PaperAgent initialCollectionId={pendingCollectionId} initialIdea={pendingIdea} initialDraftId={pendingDraftId} user={user} onMounted={() => { setPendingCollectionId(null); setPendingIdea(null); setPendingDraftId(null); }} />
        ) : (
          <>
            <SearchBar
              onSearch={handleSearch}
              initialQuery={searchQuery}
              loading={loading}
              searchOptions={searchOptions}
              onSearchOptionsChange={handleSearchOptionsChange}
            />

            {!user && !searchQuery && !loading && !results && (
              <div className="example-chips">
                {[
                  'CRISPR gene editing',
                  'transformer neural networks',
                  'climate tipping points',
                  'mRNA vaccine immunogenicity',
                  'lithium ion battery degradation',
                ].map(q => (
                  <button key={q} className="example-chip" onClick={() => handleSearch(q)}>
                    {q}
                  </button>
                ))}
              </div>
            )}

            {searchQuery && (
              <>
                {searchHistory.length > 0 && (
                  <div className="back-button-container">
                    <button className="back-button" onClick={handleBack}>
                      <span className="back-icon">←</span>
                      Back to Previous Search
                    </button>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '10px' }}>
                  <SortFilter
                    sortBy={sortBy}
                    onSortChange={handleSortChange}
                  />
                  {isAuthenticated() && (
                    <button
                      className="save-search-button"
                      onClick={() => {
                        setCurrentView('monitors');
                        const searchParams = {
                          query_string: searchQuery,
                          filters_json: JSON.stringify(filters)
                        };
                        sessionStorage.setItem('pendingMonitor', JSON.stringify(searchParams));
                      }}
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '0.9rem',
                        fontWeight: '500'
                      }}
                    >
                      <span>🔔</span> Save This Search
                    </button>
                  )}
                </div>
                <Filters
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  isLoggedIn={isAuthenticated()}
                  facets={facets}
                />
              </>
            )}

            {loading && (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Searching OpenAlex...</p>
              </div>
            )}

            {error && (
              <div className="error-container">
                <p>❌ {error}</p>
              </div>
            )}

            {!user && filteredResults && !loading && (
              <div className="guest-search-nudge">
                <span>Sign up free to save papers, build collections, and get AI-powered insights.</span>
                <button className="guest-nudge-btn" onClick={() => openAuthModal(false)}>Create account →</button>
              </div>
            )}

            {filteredResults && !loading && (
              <SearchResults
                results={filteredResults}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                perPage={perPage}
                searchQuery={searchQuery}
                onCitationSearch={handleCitationSearch}
                onSaveToCollection={handleSaveToCollection}
                isAuthenticated={isAuthenticated()}
                relevanceThreshold={filters.relevanceThreshold}
                onSignInToSave={!user ? () => openAuthModal(false) : undefined}
              />
            )}

            {!searchQuery && !loading && !results && (
              user ? (
                <div className="welcome-container">
                  <h2>🔬 Welcome to Mnevia</h2>
                  <p className="welcome-subtitle">
                    Your research direction engine — analyze your collections, draft funded-grade proposals,
                    and iterate against a calibrated reviewer panel that learns your voice.
                  </p>
                  <div className="feature-grid">
                    <div
                      className="feature-card feature-card--clickable"
                      onClick={() => setCurrentView('collections')}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setCurrentView('collections')}
                    >
                      <div className="feature-icon">📚</div>
                      <h3>Collection analysis — find your white space</h3>
                      <p>Build a collection of your papers and the literature you care about. Mnevia surfaces convergence scores, research gaps, and ranked directions you're uniquely positioned to pursue.</p>
                      <span className="feature-card-cta">Open Collections →</span>
                    </div>
                    <div
                      className="feature-card feature-card--clickable"
                      onClick={() => setCurrentView('grant-agent')}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setCurrentView('grant-agent')}
                    >
                      <div className="feature-icon">📝</div>
                      <h3>Draft → Panel Review iteration loop</h3>
                      <p>Generate a proposal grounded in your collection, then iterate against six calibrated reviewers (Significance, Innovation, Approach, Investigator, Program Officer, Skeptic). Accept, decline, or edit each rewrite — every round closes real issues.</p>
                      <span className="feature-card-cta">Open Grant Agent →</span>
                    </div>
                    <div
                      className="feature-card feature-card--clickable"
                      onClick={() => setCurrentView('pi-profile')}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setCurrentView('pi-profile')}
                    >
                      <div className="feature-icon">🧬</div>
                      <h3>Compound learning across submissions</h3>
                      <p>Upload prior funded and rejected proposals. Mnevia extracts your style fingerprint, win patterns, and recurring reviewer critiques — then pre-empts them in the next draft. Every submission makes the next one stronger.</p>
                      <span className="feature-card-cta">Open PI Profile →</span>
                    </div>
                    <div className="feature-card">
                      <div className="feature-icon">✅</div>
                      <h3>Compliance audit + verification</h3>
                      <p>Six compliance specialists audit page limits, required sections, ethics statements, budget rules, and placeholders before submission. Every revision is verified against the issue it claims to fix — resolved, partial, unresolved, or regressed.</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="landing-container">
                  {/* Hero */}
                  <div className="landing-hero">
                    <div className="landing-hero-text">
                      <h1>Your research copilot that learns how you write — and gets better every time.</h1>
                      <p className="landing-hero-sub">
                        Paste your faculty page. In 60 seconds, see your research white space,
                        your convergence scores, and start drafting proposals grounded in your own evidence.
                      </p>
                      <div className="landing-hero-cta">
                        <button className="landing-btn landing-btn--primary" onClick={() => openAuthModal(false)}>
                          Get Started Free
                        </button>
                        <span className="landing-hero-note">No credit card required. 200K AI tokens/month free.</span>
                      </div>
                    </div>
                  </div>

                  {/* How it works */}
                  <div className="landing-steps">
                    <h2>How it works</h2>
                    <div className="landing-steps-grid">
                      <div className="landing-step">
                        <div className="landing-step-num">1</div>
                        <h3>Build your collection</h3>
                        <p>Paste your faculty page or ORCID. Add the literature you care about. Mnevia ranks the directions where you're one of few in the world.</p>
                      </div>
                      <div className="landing-step">
                        <div className="landing-step-num">2</div>
                        <h3>Draft a grounded proposal</h3>
                        <p>Pick a NOFO, pick a direction. Mnevia drafts every section grounded in your real papers — every claim traceable to its source.</p>
                      </div>
                      <div className="landing-step">
                        <div className="landing-step-num">3</div>
                        <h3>Iterate against a calibrated panel</h3>
                        <p>Six expert reviewers read your NOFO and your literature, flag ranked issues, and rewrite the sections you select. Every revision is verified against the issue it claims to fix.</p>
                      </div>
                      <div className="landing-step">
                        <div className="landing-step-num">4</div>
                        <h3>Compound across submissions</h3>
                        <p>Upload prior funded and rejected proposals. Win patterns, reviewer critiques, and your style fingerprint flow into every future draft — so each submission makes the next one stronger.</p>
                      </div>
                    </div>
                  </div>

                  {/* Trust section */}
                  <div className="landing-trust">
                    <h2>Built for how federal grants actually work</h2>
                    <div className="landing-trust-grid">
                      <div className="landing-trust-item">
                        <strong>Source-grounded</strong>
                        <p>Every paragraph cites your real papers. Hover any citation to see why it was used.</p>
                      </div>
                      <div className="landing-trust-item">
                        <strong>You decide everything</strong>
                        <p>3-way choice on every revision: keep original, keep revised, or write your own. Nothing auto-accepted.</p>
                      </div>
                      <div className="landing-trust-item">
                        <strong>Compliance checked</strong>
                        <p>Page limits, required sections, ethics statements, data plans — verified before you submit.</p>
                      </div>
                      <div className="landing-trust-item">
                        <strong>Compounds over time</strong>
                        <p>Win patterns, reviewer critiques, style fingerprint — every submission makes the next one better.</p>
                      </div>
                    </div>
                  </div>

                  {/* Final CTA */}
                  <div className="landing-final-cta">
                    <h2>Ready to find your white space?</h2>
                    <p>Join PIs who draft proposals in their own voice, grounded in their own evidence.</p>
                    <button className="landing-btn landing-btn--primary" onClick={() => openAuthModal(false)}>
                      Sign Up Free
                    </button>
                  </div>

                  <div className="landing-footer">
                    <a href="/security.html">Security &amp; Data Practices</a>
                    <a href="/dpa.html">Data Processing Agreement</a>
                    <a href="mailto:privacy@mnevia.com">privacy@mnevia.com</a>
                  </div>
                </div>
              )
            )}
          </>
        )}
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLoginSuccess={handleLoginSuccess}
        initialIsLogin={authModalInitialIsLogin}
      />

      <SaveToCollectionModal
        isOpen={showSaveModal}
        onClose={() => {
          setShowSaveModal(false);
          setWorkToSave(null);
        }}
        work={workToSave}
        onSave={handleSaveSuccess}
      />

      <RoleSelectionModal
        isOpen={showRoleModal}
        onRoleSelected={handleRoleSelected}
      />

      <SupportModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        user={user}
      />

      <WhatsNewModal
        isOpen={showWhatsNewModal}
        onClose={handleWhatsNewClose}
        lastSeenVersion={lastSeenVersion}
      />
    </div>
    </ThemeProvider>
  );
}

export default App;

